import { useMutation, useQuery } from '@tanstack/react-query';
import { Edit2, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { customerAPI, fitnessAPI } from '../api/index';
import { queryClient } from '../App';
import { Button } from '../components/Button';
import { ExpiryBadge } from '../components/ExpiryBadge';
import AddFitnessModal from '../components/modals/AddFitnessModal';
import { PageHeader } from '../components/PageHeader';
import { daysRemaining, formatDate } from '../lib/utils';
import type { FitnessCertificate } from '../types/index';

export function FitnessPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<'all' | 'active' | 'expiring_soon' | 'expired'>('all')
  const [offset, setOffset] = useState(0)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [showCustomerPicker, setShowCustomerPicker] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [selectedFitness, setSelectedFitness] = useState<FitnessCertificate | undefined>()
  const [customerSearch, setCustomerSearch] = useState('')

  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['customers', 'fitness-picker'],
    queryFn: () =>
      customerAPI.list({
        limit: 200,
        offset: 0,
        active: true,
      }).then((r) => r.data),
  })

  const customers = customersData?.customers || []
  const filteredCustomers = customers.filter((customer) =>
    `${customer.full_name} ${customer.phone}`.toLowerCase().includes(customerSearch.toLowerCase())
  )

  const { data, isLoading } = useQuery({
    queryKey: ['fitness', status, offset],
    queryFn: () =>
      fitnessAPI.list({
        status: status === 'all' ? undefined : status,
        limit: 20,
        offset,
      }).then(r => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => fitnessAPI.delete(id),
    onSuccess: () => {
      toast.success('Fitness certificate deleted')
      queryClient.invalidateQueries({ queryKey: ['fitness'] })
      setDeleteId(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to delete')
    },
  })

  const total = data?.total || 0
  const fitness = data?.fitness || []
  const totalPages = Math.ceil(total / 20)
  const currentPage = Math.floor(offset / 20) + 1

  const tabs = [
    { id: 'all' as const, label: 'All' },
    { id: 'active' as const, label: 'Active' },
    { id: 'expiring_soon' as const, label: 'Expiring Soon' },
    { id: 'expired' as const, label: 'Expired' },
  ]

  const handleAddFitness = () => {
    setShowCustomerPicker(true)
  }

  const handleEditFitness = (fitness: FitnessCertificate) => {
    setSelectedFitness(fitness)
    setSelectedCustomerId(fitness.customer_id)
    setShowAddModal(true)
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setSelectedFitness(undefined)
    setSelectedCustomerId('')
  }

  const handleSelectCustomer = (customerId: string) => {
    setSelectedCustomerId(customerId)
    setShowCustomerPicker(false)
    setShowAddModal(true)
  }

  return (
    <>
      {/* Customer Picker Modal */}
      {showCustomerPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full max-h-96 flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Select Customer</h2>
              <button onClick={() => setShowCustomerPicker(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded mb-4"
            />
            <div className="flex-1 overflow-y-auto">
              {customersLoading ? (
                <div className="text-center py-4 text-gray-500">Loading...</div>
              ) : filteredCustomers.length > 0 ? (
                <div className="space-y-2">
                  {filteredCustomers.map((customer) => (
                    <button
                      key={customer.id}
                      onClick={() => handleSelectCustomer(customer.id)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded"
                    >
                      <div className="font-medium">{customer.full_name}</div>
                      <div className="text-sm text-gray-600">{customer.phone}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">No customers found</div>
              )}
            </div>
          </div>
        </div>
      )}

      <PageHeader title="Fitness Certificates" action={{ label: 'Add Fitness', onClick: handleAddFitness }} />

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setStatus(tab.id); setOffset(0) }}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              status === tab.id
                ? 'border-saffron text-saffron'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : fitness.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="mb-4">No fitness certificates found</p>
              <Button onClick={handleAddFitness} className="bg-saffron">
                <Plus className="w-4 h-4 mr-2" />
                Create Fitness Certificate
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4">Customer</th>
                  <th className="text-left py-3 px-4">Vehicle</th>
                  <th className="text-left py-3 px-4">Cert #</th>
                  <th className="text-left py-3 px-4">Class</th>
                  <th className="text-left py-3 px-4">Inspection Date</th>
                  <th className="text-left py-3 px-4">Expiry</th>
                  <th className="text-left py-3 px-4">Result</th>
                  <th className="text-left py-3 px-4">Renewal Due</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {fitness.map((cert: any) => (
                  <tr key={cert.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <button onClick={() => navigate(`/customers/${cert.customer_id}`)} className="text-saffron hover:underline">
                        {cert.customer_name}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm">{cert.vehicle_reg}</td>
                    <td className="py-3 px-4 font-medium">{cert.certificate_number}</td>
                    <td className="py-3 px-4"><span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{cert.fitness_class}</span></td>
                    <td className="py-3 px-4">{formatDate(cert.inspection_date)}</td>
                    <td className="py-3 px-4"><ExpiryBadge days={daysRemaining(cert.expiry_date)} /></td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded ${cert.result === 'pass' ? 'bg-green-100 text-green-800' : cert.result === 'fail' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {cert.result}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">{formatDate(cert.renewal_due_date)}</td>
                    <td className="py-3 px-4 text-right flex justify-end gap-2">
                      <Button size="sm" variant="secondary" onClick={() => handleEditFitness(cert)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setDeleteId(cert.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && fitness.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">Showing {Math.min(offset + 20, total)} of {total}</p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 20))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setOffset(offset + 20)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm">
            <p className="text-gray-900 font-medium mb-4">Delete this fitness certificate?</p>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => deleteMutation.mutate(deleteId)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      <AddFitnessModal
        open={showAddModal}
        onClose={handleCloseModal}
        customerId={selectedCustomerId}
        fitness={selectedFitness}
        onSuccess={handleCloseModal}
      />
    </>
  )
}
